import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  getWorkspaceComplianceProfile,
  getWorkspaceOwnershipRules,
  updateWorkspaceComplianceProfile,
  updateWorkspaceOwnershipRules,
} from "../../api/workspaceCompliance";
import WorkspaceComplianceFrame from "../../components/workspaceCompliance/WorkspaceComplianceFrame";
import {
  BooleanCard,
  fieldClass,
  InputLabel,
  SectionCard,
  textareaClass,
  WarningBanner,
} from "../../components/workspaceCompliance/WorkspaceComplianceUi";
import { useAuth } from "../../context/AuthContext";
import useOperatorWorkspace from "../../hooks/useOperatorWorkspace";
import {
  approvalModeOptions,
  clientOwnershipOptions,
  controlOptions,
  occupancyModeOptions,
  productProviderOptions,
  rentModelOptions,
  titleCaseComplianceLabel,
} from "../../constants/workspaceCompliance";
import { getAdvancedRecordTooltip } from "../../components/workspaceCompliance/advancedRecordsHelpContent";

const defaultForm = {
  name: "",
  approval_mode: "manual",
  rent_model: "flat_rent",
  price_control: "renter",
  schedule_control: "renter",
  product_provider: "renter",
  allows_independent_products: true,
  client_ownership_mode: "independent_owned",
  can_work_elsewhere: true,
  uses_own_business_name: true,
  collects_own_payments: true,
  mandatory_desk_shifts: false,
  fixed_hours_required: false,
  notes: "",
  risk_flags_json: {},
};

const defaultOwnershipRules = {
  default_occupancy_mode: "",
  default_client_ownership_mode: "independent_owned",
  payment_collector: "renter",
  requires_agreement_acknowledgement: true,
  allow_renter_guest_checkout: false,
};

function SummaryStat({ label, value }) {
  return (
    <article className="min-w-0 rounded-2xl border border-slate-200 bg-white px-4 py-4 shadow-sm">
      <div className="break-words text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
        {label}
      </div>
      <div className="mt-2 break-words text-base font-semibold text-slate-900">{value}</div>
    </article>
  );
}

function RelationshipCard({ option, selected, onSelect }) {
  const active = option.value === selected;
  return (
    <button
      type="button"
      onClick={() => onSelect(option.value)}
      className={`min-w-0 rounded-3xl border p-5 text-left transition ${
        active
          ? "border-rose-300 bg-rose-50 shadow-sm"
          : "border-slate-200 bg-white hover:border-slate-300"
      }`}
    >
      <div className="break-words text-sm font-semibold text-slate-900">{option.label}</div>
      <p className="mt-2 break-words text-sm leading-6 text-slate-600">{option.description}</p>
    </button>
  );
}

export default function OperatorWorkspaceCompliancePage() {
  const { id } = useParams();
  const { token } = useAuth();
  const {
    workspace,
    loading: workspaceLoading,
    error: workspaceError,
  } = useOperatorWorkspace(id);
  const [form, setForm] = useState(defaultForm);
  const [profile, setProfile] = useState(null);
  const [ownershipRules, setOwnershipRules] = useState(defaultOwnershipRules);
  const [relationshipMode, setRelationshipMode] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [flash, setFlash] = useState("");

  useEffect(() => {
    if (!token || !id) return undefined;
    let active = true;
    setLoading(true);
    setError("");

    Promise.all([
      getWorkspaceComplianceProfile(id, token),
      getWorkspaceOwnershipRules(id, token).catch(() => null),
    ])
      .then(([profileResponse, ownershipResponse]) => {
        if (!active) return;
        const nextProfile = profileResponse || {};
        const nextOwnershipRules = {
          ...defaultOwnershipRules,
          ...(ownershipResponse || {}),
        };
        setProfile(nextProfile);
        setOwnershipRules(nextOwnershipRules);
        setRelationshipMode(nextOwnershipRules.default_occupancy_mode || "");
        setForm({
          ...defaultForm,
          ...nextProfile,
          name:
            nextProfile.name ||
            workspace?.booth_name ||
            workspace?.name ||
            "Workspace compliance profile",
          notes: nextProfile.notes || "",
          risk_flags_json:
            nextProfile.risk_flags_json && typeof nextProfile.risk_flags_json === "object"
              ? nextProfile.risk_flags_json
              : {},
        });
      })
      .catch((err) => {
        if (active) {
          setError(err.message || "Unable to load workspace compliance profile.");
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [id, token, workspace?.booth_name, workspace?.name]);

  const handleFieldChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!token || !id) return;
    setSaving(true);
    setError("");
    setFlash("");
    try {
      const [savedProfile, savedOwnershipRules] = await Promise.all([
        updateWorkspaceComplianceProfile(
          id,
          {
            ...form,
            notes: form.notes || null,
            risk_flags_json:
              form.risk_flags_json && typeof form.risk_flags_json === "object"
                ? form.risk_flags_json
                : {},
          },
          token
        ),
        relationshipMode
          ? updateWorkspaceOwnershipRules(
              id,
              {
                ...ownershipRules,
                default_occupancy_mode: relationshipMode,
              },
              token
            )
          : Promise.resolve(ownershipRules),
      ]);
      setProfile(savedProfile);
      setOwnershipRules((prev) => ({
        ...prev,
        ...(savedOwnershipRules || {}),
        default_occupancy_mode:
          savedOwnershipRules?.default_occupancy_mode || relationshipMode || "",
      }));
      setRelationshipMode(
        savedOwnershipRules?.default_occupancy_mode || relationshipMode || ""
      );
      setForm((prev) => ({
        ...prev,
        ...savedProfile,
        notes: savedProfile.notes || "",
      }));
      setFlash("Workspace compliance profile saved.");
    } catch (err) {
      setError(err.message || "Unable to save workspace compliance profile.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <WorkspaceComplianceFrame
      workspace={workspace}
      workspaceId={id}
      title="Setup Checks"
      description="Review the deeper renter-rule settings that support the rental listing setup."
    >
      {workspaceError ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {workspaceError}
        </div>
      ) : null}
      {error ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      ) : null}
      {flash ? (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
          {flash}
        </div>
      ) : null}

      <WarningBanner
        strictModeEnabled={Boolean(profile?.strict_mode_enabled)}
        publishBlocked={Boolean(profile?.publish_blocked)}
        agreementCompletionBlocked={Boolean(profile?.agreement_completion_blocked)}
        warningStateLevel={profile?.warning_state_level || "none"}
        warnings={profile?.warnings || []}
        disclaimer
      />

      <section className="grid scroll-mt-28 gap-4 sm:grid-cols-2 xl:grid-cols-5">
        <SummaryStat
          label="Warning level"
          value={titleCaseComplianceLabel(profile?.warning_state_level, "None")}
        />
        <SummaryStat
          label="Approval mode"
          value={titleCaseComplianceLabel(form.approval_mode)}
        />
        <SummaryStat
          label="Rent model"
          value={titleCaseComplianceLabel(form.rent_model)}
        />
        <SummaryStat
          label="Client ownership"
          value={titleCaseComplianceLabel(form.client_ownership_mode)}
        />
        <SummaryStat
          label="Operating relationship"
          value={titleCaseComplianceLabel(relationshipMode, "Not set")}
        />
      </section>

      <form className="space-y-6" onSubmit={handleSubmit}>
        <SectionCard
          title="Operating Relationship"
          description="Choose the primary rental relationship this workspace supports. This setting helps drive saved setup records, setup checks, agreement behavior, and renter-facing expectations."
          tooltip={getAdvancedRecordTooltip("Operating relationship")}
        >
          <div className="grid gap-4 lg:grid-cols-2 2xl:grid-cols-3">
            {occupancyModeOptions.map((option) => (
              <RelationshipCard
                key={option.value}
                option={option}
                selected={relationshipMode}
                onSelect={setRelationshipMode}
              />
            ))}
          </div>
          <div className="break-words rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 text-sm leading-6 text-slate-600">
            Detailed CRM, reporting, and payment defaults still live on the Ownership page. This section keeps the high-level operating relationship visible on the main compliance form.
          </div>
        </SectionCard>

        <SectionCard
          title="Rental Rule Details"
          description="Capture the business arrangement used for this workspace. These fields support setup checks shown throughout MyBoothShare."
          tooltip="Use these fields to align the rental setup with how the workspace is actually operated."
        >
          {loading || workspaceLoading ? (
            <p className="text-sm text-slate-600">Loading workspace compliance profile...</p>
          ) : null}
          <div className="grid gap-4 md:grid-cols-2">
            <InputLabel
              label="Profile name"
              hint="Use a practical internal label such as Independent chair baseline or Host-operated suite."
              tooltip={getAdvancedRecordTooltip("Profile name")}
            >
              <input
                className={fieldClass}
                value={form.name}
                onChange={(event) => handleFieldChange("name", event.target.value)}
                required
              />
            </InputLabel>
            <InputLabel label="Approval mode" tooltip={getAdvancedRecordTooltip("Approval mode")}>
              <select
                className={fieldClass}
                value={form.approval_mode}
                onChange={(event) => handleFieldChange("approval_mode", event.target.value)}
              >
                {approvalModeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </InputLabel>
            <InputLabel
              label="Rent model"
              hint="Flat rent usually aligns more closely with an independent rental posture than percentage-only compensation."
              tooltip={getAdvancedRecordTooltip("Rent model")}
            >
              <select
                className={fieldClass}
                value={form.rent_model}
                onChange={(event) => handleFieldChange("rent_model", event.target.value)}
              >
                {rentModelOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </InputLabel>
            <InputLabel
              label="Who owns clients"
              hint="This should match the operational reality operators expect for CRM visibility and long-term reporting."
              tooltip={getAdvancedRecordTooltip("Who owns clients")}
            >
              <select
                className={fieldClass}
                value={form.client_ownership_mode}
                onChange={(event) =>
                  handleFieldChange("client_ownership_mode", event.target.value)
                }
              >
                {clientOwnershipOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </InputLabel>
            <InputLabel
              label="Who sets service prices"
              hint="If the host salon sets prices while the workspace is marked independent, warnings may appear."
              tooltip={getAdvancedRecordTooltip("Who sets service prices")}
            >
              <select
                className={fieldClass}
                value={form.price_control}
                onChange={(event) => handleFieldChange("price_control", event.target.value)}
              >
                {controlOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </InputLabel>
            <InputLabel
              label="Who controls schedule"
              hint="Use this for the day-to-day operational schedule, not just listing availability."
              tooltip={getAdvancedRecordTooltip("Who controls schedule")}
            >
              <select
                className={fieldClass}
                value={form.schedule_control}
                onChange={(event) => handleFieldChange("schedule_control", event.target.value)}
              >
                {controlOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </InputLabel>
            <InputLabel
              label="Who provides products"
              hint="Shared arrangements are valid, but stronger host-only product control can create friction for independent setups."
              tooltip={getAdvancedRecordTooltip("Who provides products")}
            >
              <select
                className={fieldClass}
                value={form.product_provider}
                onChange={(event) => handleFieldChange("product_provider", event.target.value)}
              >
                {productProviderOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </InputLabel>
            <InputLabel
              label="Notes"
              hint="Use this field for operational context, internal review notes, or rollout exceptions. Do not use it to make legal conclusions."
              tooltip={getAdvancedRecordTooltip("Notes")}
            >
              <textarea
                className={textareaClass}
                value={form.notes}
                onChange={(event) => handleFieldChange("notes", event.target.value)}
              />
            </InputLabel>
          </div>
        </SectionCard>

        <SectionCard
          title="Renter Business Rules"
          description="These answers clarify how independently the renter operates inside the workspace."
          tooltip="These questions help distinguish a workspace rental from a salon-controlled staff arrangement."
        >
          <div className="grid gap-4">
            <BooleanCard
              label="Can the renter work elsewhere?"
              tooltip={getAdvancedRecordTooltip("Can the renter work elsewhere?")}
              checked={Boolean(form.can_work_elsewhere)}
              onChange={(value) => handleFieldChange("can_work_elsewhere", value)}
            />
            <BooleanCard
              label="Can the renter use their own business branding?"
              tooltip={getAdvancedRecordTooltip("Can the renter use their own business branding?")}
              checked={Boolean(form.uses_own_business_name)}
              onChange={(value) => handleFieldChange("uses_own_business_name", value)}
            />
            <BooleanCard
              label="Are payments collected directly by the renter?"
              description="Choose No when the host salon or platform controls client-facing collection."
              tooltip={getAdvancedRecordTooltip("Are payments collected directly by the renter?")}
              checked={Boolean(form.collects_own_payments)}
              onChange={(value) => handleFieldChange("collects_own_payments", value)}
              trueLabel="Directly"
              falseLabel="Through salon / platform"
            />
            <BooleanCard
              label="Can the renter use independent products?"
              description="Use this to distinguish salon-only product requirements from mixed or independent supply arrangements."
              tooltip={getAdvancedRecordTooltip("Can the renter use independent products?")}
              checked={Boolean(form.allows_independent_products)}
              onChange={(value) => handleFieldChange("allows_independent_products", value)}
            />
            <BooleanCard
              label="Can the salon require desk shifts?"
              tooltip={getAdvancedRecordTooltip("Can the salon require desk shifts?")}
              checked={Boolean(form.mandatory_desk_shifts)}
              onChange={(value) => handleFieldChange("mandatory_desk_shifts", value)}
            />
            <BooleanCard
              label="Can the salon require fixed hours?"
              tooltip={getAdvancedRecordTooltip("Can the salon require fixed hours?")}
              checked={Boolean(form.fixed_hours_required)}
              onChange={(value) => handleFieldChange("fixed_hours_required", value)}
            />
          </div>
        </SectionCard>

        <div className="flex flex-wrap gap-3">
          <button
            type="submit"
            disabled={saving}
            className="rounded-full bg-rose-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-rose-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {saving ? "Saving..." : "Save Setup Checks"}
          </button>
        </div>
      </form>
    </WorkspaceComplianceFrame>
  );
}
